import { useLocation, Link } from "react-router-dom";
import articles from "@/content/ts-articles";
import { useSeo, breadcrumbSchema } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";

const BY_PATH = Object.fromEntries(articles.records.map((r) => [r.path, r]));

// Prose styling for the supplied, verbatim article HTML (one H1 rendered above).
const BODY_CLASS =
  "max-w-3xl text-charcoal/80 space-y-5 " +
  "[&_h2]:font-serif [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:text-deep-navy [&_h2]:leading-snug [&_h2]:mt-12 [&_h2]:mb-4 " +
  "[&_p]:text-lg [&_p]:leading-relaxed " +
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:text-lg [&_li]:leading-relaxed " +
  "[&_a]:text-restrained-blue [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-plum";

export default function ArticlePage() {
  const { pathname } = useLocation();
  const rec = BY_PATH[pathname.replace(/\/+$/, "") || pathname];

  const m = rec?.metadata;
  const crumbs = rec
    ? [{ name: "Home", path: "/" }, rec.parent, { name: m.h1, path: rec.path }].filter(Boolean)
    : [];

  useSeo({
    title: m?.title,
    description: m?.meta_description,
    path: rec?.path || pathname,
    canonical: m?.canonical || null,
    schema: rec
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: m.h1,
            description: m.meta_description,
            provider: { "@type": "Organization", name: "ThinkStack", areaServed: "Singapore" },
            areaServed: "Singapore",
          },
          breadcrumbSchema(crumbs),
        ]
      : null,
  });

  if (!rec) return null;

  return (
    <article data-testid="ts-article">
      <section className="relative text-white overflow-hidden" style={{ backgroundColor: "#0B1B34" }}>
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-16 md:pt-20 md:pb-20">
          <nav className="text-xs text-white/70 mb-6 flex flex-wrap gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            {rec.parent && (
              <span>/ <Link to={rec.parent.path} className="hover:text-white">{rec.parent.name}</Link></span>
            )}
          </nav>
          <Reveal>
            <h1 data-testid="article-h1" className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.04] max-w-4xl">
              {m.h1}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-14 md:py-20">
          <Reveal>
            <div
              data-testid="article-body"
              className={BODY_CLASS}
              dangerouslySetInnerHTML={{ __html: rec.body_html_without_h1 }}
            />
          </Reveal>
        </div>
      </section>
    </article>
  );
}
